import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { Toast } from "@/components/common";
import {
  createLogoSignedUrl,
  getPreference,
  removeLogo,
  updatePreference,
  uploadLogoToSignedUrl,
} from "@/services/http/members";
import { setThemeInCSS } from "@/utils/setThemeColors";
import { PreferenceThemeColorsEnum, PreferenceThemeSlugsEnum } from "@_types/preferences/Preference.enums";
import { UpdatePreferenceInput } from "@_types/preferences/Preference.use-cases";

import { CreateSignedUrlInput, UploadSignedUrlInput } from "@_types/shared";
import { isAxiosError } from "axios";
import { PreferencesActions, PreferencesStoreProps } from "./Preferences.types";

const initialState: PreferencesStoreProps = {
  uuid: "",
  logo: null,
  logoFiles: null,
  themeSlug: PreferenceThemeSlugsEnum.CLEAN,
  customized: false,
  color: PreferenceThemeColorsEnum.CLEAN,
  isLoading: false,
  isUpdating: false,
  temporaryLogo: "https://github.com/twbs.png",
  isUploadModalOpen: false,
};

export const usePreferencesStore = create<PreferencesActions & PreferencesStoreProps>()(
  devtools((set, get) => ({
    ...initialState,

    openUploadModal: () => set({ isUploadModalOpen: true }),
    closeUploadModal: () => set({ isUploadModalOpen: false }),
    setImageFiles: logoFiles => set({ logoFiles }),

    setPreferences: data => {
      const { ...preferencesData }: UpdatePreferenceInput = data;
      set({ ...preferencesData });
    },

    fetchPreferences: async (courseUuid: string) => {
      set({ isLoading: true });
      try {
        const { data: axiosData } = await getPreference({ uuid: courseUuid });

        const { color, customized, themeSlug } = axiosData;

        setThemeInCSS(customized, themeSlug, color);

        set({
          ...axiosData,
          logo: axiosData.logo,
        });
      } catch (error) {
        Toast.error("Erro ao carregar preferências", "Tente novamente mais tarde");
      } finally {
        set({ isLoading: false });
      }
    },

    updatePreferences: async (courseUuid, preferences) => {
      set({ isLoading: true });
      const configOld = get();
      set(state => ({ ...state, ...preferences }));
      try {
        const { data: axiosData } = await updatePreference({ ...preferences, uuid: courseUuid });

        const { color, customized, themeSlug } = axiosData;

        setThemeInCSS(customized, themeSlug, color);

        set({ ...axiosData });
      } catch (error) {
        Toast.error("Erro ao atualizar preferências", "Tente novamente mais tarde");
        set(state => ({ ...state, ...configOld }));
      } finally {
        set({ isLoading: false });
      }
    },

    uploadLogo: async (courseUuid: string, file: File) => {
      const mimetype = file.type;
      const size = file.size;
      const originalname = `logo-${file.name.replace(/\s/g, "").toLocaleLowerCase().replace(/\d/g, "")}`;

      const bodyData: CreateSignedUrlInput = {
        mimetype,
        originalname,
        size,
      };

      try {
        set({ isUpdating: true });

        const { data: response } = await createLogoSignedUrl(courseUuid, bodyData);
        const formData: UploadSignedUrlInput = { ...response.data, file };

        try {
          await uploadLogoToSignedUrl(response.url, formData);
          set({ logo: URL.createObjectURL(file) });
          Toast.success("Upload concluído", "Logo atualizado com sucesso");
        } catch (error) {
          if (isAxiosError(error)) throw new Error(error.response?.data?.message, {});
        }
      } catch (error) {
        if (isAxiosError(error)) throw new Error(error.response?.data?.message, {});
      } finally {
        set({ isUpdating: false });
      }
    },

    removeLogo: async (courseUuid: string) => {
      set({ isUpdating: true });

      try {
        await removeLogo({ uuid: courseUuid });
        set({ logo: null });
      } catch (error) {
        if (isAxiosError(error)) throw new Error(error.response?.data?.message, {});
      } finally {
        set({ isUpdating: false });
      }
    },
  }))
);
